import { useState } from 'react';

/**
 * Custom hook to manage Organizational Metrics Modal state and functionality
 * This hook handles opening the modal when organizational analytics card details are clicked
 */
export const useOrganizationalMetricsModal = () => {
  const [modalState, setModalState] = useState({
    show: false,
    data: [],
    title: 'Improve staff wellbeing',
    date: null,
    cardType: 'green'
  });

  /**
   * Opens the modal with provided configuration
   * @param {Object} config - Modal configuration
   * @param {Array} config.data - Data to display in the modal table
   * @param {string} config.title - Modal title
   * @param {string|Date} config.date - Date to display
   * @param {string} config.cardType - Type of card clicked (green, amber, red)
   */
  const openModal = (config = {}) => {
    setModalState({
      show: true,
      data: config.data || [],
      title: config.title || getModalTitle(config.cardType || 'green'),
      date: config.date || new Date().toISOString(),
      cardType: config.cardType || 'green'
    });
  };

  /**
   * Closes the modal and resets state
   */
  const closeModal = () => {
    setModalState(prev => ({
      ...prev,
      show: false
    }));
  };

  /**
   * Handle metrics card click with automatic modal opening
   * @param {string} cardType - The type of card clicked (green, amber, red, metrics)
   * @param {Object} options - Additional options like custom title, data, date
   */
  const handleMetricsCardClick = (cardType, options = {}) => {
    const modalData = generateModalData(cardType);
    const modalTitle = options.title || getModalTitle(cardType);

    openModal({
      data: modalData,
      title: modalTitle,
      date: options.date || new Date().toISOString(),
      cardType: cardType
    });
  };

  /**
   * Generate modal title based on card type
   * @param {string} cardType - The card type
   * @returns {string} Modal title
   */
  const getModalTitle = (cardType) => {
    switch (cardType) {
      case 'green':
        return 'Improve staff wellbeing';
      case 'amber':
        return 'Staff wellbeing attention required';
      case 'red':
        return 'Staff wellbeing critical';
      case 'metrics':
        return 'Metrics Considered';
      default:
        return 'Improve staff wellbeing';
    }
  };

  /**
   * Generate modal data based on card type
   * @param {string} cardType - The card type
   * @returns {Array} Modal data
   */
  const generateModalData = (cardType) => {
    const baseMetrics = [
      'Completed Calls',
      'Email Reply Time (hrs.)',
      'CSAT (%)',
      'Repeated Calls (%)',
      'Manager Rating',
      'NPS',
    ];

    // Normalize cardType to one of the accepted values
    const normalizedType = ['green', 'amber', 'red'].includes(cardType) ? cardType : 'green';

    // Set all metrics to the selected risk level so the modal shows only that level
    return baseMetrics.map(metric => ({
      metricsName: metric,
      riskLevel: normalizedType
    }));
  };

  return {
    modalState,
    openModal,
    closeModal,
    handleMetricsCardClick
  };
};

export default useOrganizationalMetricsModal;
